import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subscription } from 'rxjs';
import { debounceTime, skip, tap } from 'rxjs/operators';
import { BaseComponent } from '../../shared/components/base.component';
import { ApiService } from '../../shared/services/api.service';
import { IApplicationFiltersExtended } from '../../shared/shared.definitions';
import { CollectionFilterService } from './collection-filter.service';
import { CollectionMapService } from './collection-map.service';

const MAX_NUM_OF_COLLECTION = 999;

// Hardcoded UUID override for the English /collection/accommodations page.
// The backend currently returns a different uuid in the content response,
// so we swap it here to fetch the correct applications list.
const EN_ACCOMMODATIONS_COLLECTION_SLUG = 'accommodations';
const EN_ACCOMMODATIONS_COLLECTION_UUID_OVERRIDE = 'fb23499a-b962-435e-b014-8ae01cc05bdd';

@Component({
  selector: 'app-application-collection-collection',
  templateUrl: './application-collection-feature.component.html',
  styleUrls: ['./application-collection-feature.component.scss'],
})
export class ApplicationCollectionFeatureComponent extends BaseComponent implements OnInit, OnDestroy {
  subtitle = 'listing page subtitle';
  applications: any[];
  applicationLength: number;
  applicationMaxLength: number;
  applicationsPerPage = 12;
  limitMultiplier = 1;
  type: string;
  uuid: string;
  searchControl: UntypedFormControl;
  title: string;
  isMobile = false;
  showFilters = false;
  seed: number | null = null;
  private isInitialFilterEmissionHandled = false;

  private readonly mapFilterHandler$ = this.collectioMapService.isMapOpen$.pipe(
    skip(1),
    tap((isMapOpen: boolean) => {
      if (isMapOpen) {
        this.filterMapRequest();
      } else {
        this.filterRequest(false);
      }
    })
  );
  private mapFilterHandlerSub: Subscription | null = null;
  private searchControlValueSub: Subscription | null = null;

  constructor(
    private readonly apiService: ApiService,
    private readonly translateService: TranslateService,
    readonly filterService: CollectionFilterService,
    private readonly collectioMapService: CollectionMapService,
    private readonly router: Router
  ) {
    super();
  }

  ngOnInit(): void {
    const pageTypes = [
      { key: 'contentPageOnlyCampaign', type: 'campaign' },
      { key: 'contentPageOnlyRestaurant', type: 'restaurant' },
      { key: 'contentPageOnlySummerCampaign', type: 'summer-accommodation' },
      { key: 'contentPageOnlyEverySeasonCampaign', type: 'every-season' },
      { key: 'contentPageOnlyAccommodation', type: 'accommodation' },
    ];

    for (const pageType of pageTypes) {
      const content = this.componentObject?.[pageType.key];
      if (content?.uuid) {
        this.type = pageType.type;
        this.uuid = this.resolveApplicationsUuid(pageType.type, content.uuid);
        this.title = content.title;
        this.defaultRequest();
        break;
      }
    }

    this.checkScreenSize();

    if (this.isMobile) {
      window.addEventListener('scroll', this.preventScrollClose, true);
    }

    this.filterService.filters$.pipe(debounceTime(300)).subscribe(() => {
      if (!this.isInitialFilterEmissionHandled) {
        this.isInitialFilterEmissionHandled = true;
        return;
      }
      if (this.collectioMapService.isMapOpenValue) {
        this.filterMapRequest();
      } else {
        this.filterRequest(false);
      }
    });

    if (this.searchControl) {
      this.searchControlValueSub = this.searchControl.valueChanges.subscribe((value) => {
        if (value === '') {
          this.onSearch();
        }
      });
    }

    this.mapFilterHandlerSub = this.mapFilterHandler$.subscribe();
  }

  get isMapOpen$(): Observable<boolean> {
    return this.collectioMapService.isMapOpen$;
  }

  checkScreenSize(): void {
    this.isMobile = window.innerWidth <= 768;
    this.showFilters = !this.isMobile;
  }

  preventScrollClose(event: Event): void {
    if (this.showFilters) {
      event.stopPropagation();
    }
  }

  onTouchStart(event: TouchEvent): void {
    if (this.isMobile && this.showFilters) {
      event.stopPropagation();
    }
  }

  defaultRequest(): void {
    const filters: IApplicationFiltersExtended = {
      search: '',
      destinations: [],
      settlements: [],
      serviceTypes: [],
      summerServices: [],
      acceptsCardType: [],
      accommodationType: [],
      accommodationClass: [],
      accommodationFeature: [],
      accommodationService: [],
      campaignOffers: [],
      discountPercent: null,
      accommodationNature: {
        accommodationForFamilies: false,
        accommodationForCouples: false,
        isAccessible: false,
      },
      paymentMethod: [],
      equipment: {
        accommodationForFamilies: false,
        accommodationForCouples: false,
        isAccessible: false,
        acceptsSzepCard: false,
        has24HourReception: false,
        hasRestaurant: false,
        idealForCouples: false,
        petFriendly: false,
        freeParking: false,
        paidParking: false,
        freeWifi: false,
        hasElevator: false,
        hasWellnessServices: false,
        hasPrivateBeach: false,
        hasOutdoorPool: false,
        hasIndoorPool: false,
        hasJacuzzi: false,
        hasSauna: false,
        hasInfraSauna: false,
        hasFitness: false,
        familyFriendly: false,
        hasOutdoorPlayground: false,
        hasIndoorPlayArea: false,
        hasKidsPool: false,
        hasCrib: false,
        hasChildcare: false,
      },
      suburb: [],
      city: [],
      priceRange: null,
    };

    if (this.type === 'summer-accommodation') {
      filters.summerServices = [
        { id: 'typeAccommodation', text: this.translateService.instant(`CAMPAIGN.type.accommodation`), checked: false },
        { id: 'typeCateringUnit', text: this.translateService.instant(`CAMPAIGN.type.cateringunit`), checked: false },
        { id: 'typeAttraction', text: this.translateService.instant(`CAMPAIGN.type.attraction`), checked: false },
        { id: 'typeBathingPlace', text: this.translateService.instant(`CAMPAIGN.type.bathingplace`), checked: false },
      ];
    }

    this.searchControl = new UntypedFormControl(filters.search);

    this.applications = [];
    this.seed = this.generateSeed();

    if (!this.component?.uuid) return;

    this.apiService
      .getApplicationsCollection$(
        this.type,
        this.uuid,
        this.component.uuid,
        0,
        this.applicationsPerPage * this.limitMultiplier,
        undefined, // term
        undefined, // destinations
        undefined, // serviceType
        undefined, // summerServices
        undefined, // acceptsCardType
        undefined, // accommodationType
        undefined, // accommodationClass
        undefined, // accommodationNature
        undefined, // paymentMethod
        undefined, // equipment
        undefined, // suburb
        undefined, // city
        undefined, // accommodationFeature
        undefined, // accommodationService
        undefined, // campaignOffers
        undefined, // discountPercent
        undefined, // priceRange
        this.seed  // seed
      )
      .subscribe(
        (res) => {
        this.filterService.updateVisibleFiltersFromMeta(res?.meta);
        if (res?.meta?.filterOptions?.destinationTag_filter) {
          Object.keys(res.meta.filterOptions?.destinationTag_filter).forEach((key) => {
            filters.destinations.push({
              id: +key,
              text: this.translateService.instant(`CAMPAIGN.destination.${res.meta.filterOptions.destinationTag_filter[key]}`),
              checked: false,
            });
          });
        }
        if (res?.meta?.filterOptions?.serviceTypes_filter) {
          Object.keys(res.meta.filterOptions?.serviceTypes_filter).forEach((key) => {
            filters.serviceTypes.push({
              id: +key,
              text: res.meta.filterOptions.serviceTypes_filter[key],
              checked: false,
            });
          });
        }

        if (res?.meta?.filterOptions?.acceptsCardType) {
          Object.keys(res.meta.filterOptions?.acceptsCardType).forEach((key) => {
            filters.acceptsCardType.push({
              id: +key,
              text: res.meta.filterOptions.acceptsCardType[key],
              checked: false,
            });
          });
        }

        if (res?.meta?.filterOptions?.accommodationType) {
          Object.keys(res.meta.filterOptions?.accommodationType).forEach((key) => {
            filters.accommodationType.push({
              id: +key,
              text: res.meta.filterOptions.accommodationType[key],
              checked: false,
            });
          });
        }

        if (res?.meta?.filterOptions?.accommodationClass) {
          Object.keys(res.meta.filterOptions?.accommodationClass).forEach((key) => {
            filters.accommodationClass.push({
              id: +key,
              text: res.meta.filterOptions.accommodationClass[key],
              checked: false,
            });
          });
        }

        if (res?.meta?.filterOptions?.accommodationFeature) {
          Object.keys(res.meta.filterOptions?.accommodationFeature).forEach((key) => {
            const numericId = Number(key);
            filters.accommodationFeature.push({
              id: Number.isNaN(numericId) ? key : numericId,
              text: res.meta.filterOptions.accommodationFeature[key],
              checked: false,
            });
          });
        }

        if (res?.meta?.filterOptions?.accommodationService) {
          Object.keys(res.meta.filterOptions?.accommodationService).forEach((key) => {
            const numericId = Number(key);
            filters.accommodationService.push({
              id: Number.isNaN(numericId) ? key : numericId,
              text: res.meta.filterOptions.accommodationService[key],
              checked: false,
            });
          });
        }

        if (res?.meta?.filterOptions?.campaignOffers) {
          Object.keys(res.meta.filterOptions?.campaignOffers).forEach((key) => {
            const numericId = Number(key);
            filters.campaignOffers.push({
              id: Number.isNaN(numericId) ? key : numericId,
              text: res.meta.filterOptions.campaignOffers[key],
              checked: false,
            });
          });
        }

        if (res?.meta?.filterOptions?.discountPercent) {
          const discountPercentOption = res.meta.filterOptions.discountPercent;
          const rawMin = Number(discountPercentOption.min ?? 0);
          const rawMax = Number(discountPercentOption.max ?? 100);
          const sanitizedMin = Number.isFinite(rawMin) ? rawMin : 0;
          const sanitizedMax = Number.isFinite(rawMax) ? rawMax : 100;
          const rawStart = Number(discountPercentOption.start ?? sanitizedMin);
          const rawEnd = Number(discountPercentOption.end ?? sanitizedMax);
          const clampedStart = Math.min(
            Math.max(Number.isFinite(rawStart) ? rawStart : sanitizedMin, sanitizedMin),
            sanitizedMax
          );
          const clampedEnd = Math.min(
            Math.max(Number.isFinite(rawEnd) ? rawEnd : sanitizedMax, sanitizedMin),
            sanitizedMax
          );

          filters.discountPercent = {
            min: sanitizedMin,
            max: sanitizedMax,
            start: Math.min(clampedStart, clampedEnd),
            end: Math.max(clampedStart, clampedEnd),
          };
        }

        if (res?.meta?.filterOptions?.suburb) {
          Object.keys(res.meta.filterOptions?.suburb).forEach((key) => {
            filters.suburb.push({
              id: +key,
              text: res.meta.filterOptions.suburb[key],
              checked: false,
            });
          });
        }

        if (res?.meta?.filterOptions?.city) {
          Object.keys(res.meta.filterOptions?.city).forEach((key) => {
            filters.city.push({
              id: key,
              text: res.meta.filterOptions.city[key],
              checked: false,
            });
          });
        }

        if (res?.meta?.filterOptions?.paymentMethod) {
          Object.keys(res.meta.filterOptions?.paymentMethod).forEach((key) => {
            filters.paymentMethod.push({
              id: +key,
              text: res.meta.filterOptions.paymentMethod[key],
              checked: false,
            });
          });
        }

        if (this.type === 'summer-accommodation') {
          filters.destinations.push({
            id: 0,
            text: this.translateService.instant('CAMPAIGN.destination.other'),
            checked: false,
          });
        }

        if (res?.meta?.filterOptions?.priceRange) {
          const minPrice = Number(res.meta.filterOptions.priceRange.min);
          const maxPrice = Number(res.meta.filterOptions.priceRange.max);
          if (Number.isFinite(minPrice) && Number.isFinite(maxPrice) && minPrice < maxPrice) {
            const step = 1000;
            const normalizedMin = Math.max(0, Math.floor(minPrice / step) * step);
            const normalizedMax = Math.ceil(maxPrice / step) * step;

            if (normalizedMin < normalizedMax) {
              filters.priceRange = {
                start: normalizedMin,
                end: normalizedMax,
                min: normalizedMin,
                max: normalizedMax,
              };
            }
          }
        }

        this.filterService.updateFilters(filters);

        // this.activeFilters = filters;
        this.applications = res.data;
        this.applicationMaxLength = res.meta.limit.allRowCount;
        this.applicationLength = this.applications.length;
      },
      () => {
        // TODO: add error handling?
      }
    );
  }

  public onSearch(): void {
    this.filterService.updateFilter('search', this.searchControl.value);
  }

  clearAllFilters(): void {
    this.searchControl.setValue('');
    this.filterService.resetFilters();
  }

  filterRequest(isAddition: boolean): void {
    const activeFilters = this.filterService.getFilters();

    if (!isAddition) {
      this.limitMultiplier = 1;
      this.seed = this.generateSeed();
    }

    const effectiveMultiplier = this.limitMultiplier;
    const fromMultiplier = isAddition ? effectiveMultiplier - 1 : 0;
    const toMultiplier = isAddition ? effectiveMultiplier : 1;

    if (!this.component?.uuid) return;

    this.apiService
      .getApplicationsCollection$(
        this.type,
        this.uuid,
        this.component.uuid,
        this.applicationsPerPage * fromMultiplier,
        this.applicationsPerPage * toMultiplier,
        activeFilters.search,
        activeFilters.destinations,
        activeFilters.serviceTypes,
        activeFilters.summerServices,
        activeFilters.acceptsCardType,
        activeFilters.accommodationType,
        activeFilters.accommodationClass,
        activeFilters.accommodationNature,
        activeFilters.paymentMethod,
        activeFilters.equipment,
        activeFilters.suburb,
        activeFilters.city,
        activeFilters.accommodationFeature,
        activeFilters.accommodationService,
        activeFilters.campaignOffers,
        activeFilters.discountPercent || undefined,
        activeFilters.priceRange || undefined,
        this.seed || undefined
      )
      .subscribe(
        (res) => {
          if (res?.meta?.limit?.seed) this.seed = res?.meta?.limit?.seed;
          this.filterService.updateVisibleFiltersFromMeta(res?.meta);
          if (isAddition) {
            this.applications = [...this.applications, ...res.data];
          } else {
            this.applications = res.data;
          }
          this.applicationMaxLength = res.meta.limit.allRowCount;
          this.applicationLength = this.applications.length;
        },
        () => {
          // TODO: Add error handling?
        }
      );
  }

  filterMapRequest(): void {
    const activeFilters = this.filterService.getFilters();

    if (!this.component?.uuid) return;

    this.apiService
      .getApplicationsCollection$(
        this.type,
        this.uuid,
        this.component.uuid,
        0,
        MAX_NUM_OF_COLLECTION,
        activeFilters.search,
        activeFilters.destinations,
        activeFilters.serviceTypes,
        activeFilters.summerServices,
        activeFilters.acceptsCardType,
        activeFilters.accommodationType,
        activeFilters.accommodationClass,
        activeFilters.accommodationNature,
        activeFilters.paymentMethod,
        activeFilters.equipment,
        activeFilters.suburb,
        activeFilters.city,
        activeFilters.accommodationFeature,
        activeFilters.accommodationService,
        activeFilters.campaignOffers,
        activeFilters.discountPercent || undefined,
        activeFilters.priceRange || undefined
      )
      .subscribe(
        (res) => {
          this.filterService.updateVisibleFiltersFromMeta(res?.meta);
          this.applications = res.data;
          this.applicationMaxLength = res.meta.limit.allRowCount;
          this.applicationLength = this.applications.length;
        },
        () => {
          // TODO: Add error handling?
        }
      );
  }

  loadMore(): void {
    this.limitMultiplier += 1;
    this.filterRequest(true);
  }

  resetLimitMultiplier(): void {
    this.limitMultiplier = 1;
  }

  toggleMap(): void {
    this.collectioMapService.toggleMap();
  }

  scrollToTop(): void {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  private generateSeed(): number {
    return Math.floor(Math.random() * 1_000_000) + 1;
  }

  private resolveApplicationsUuid(type: string, uuid: string): string {
    if (type !== 'accommodation') return uuid;
    if (this.translateService.currentLang !== 'en') return uuid;

    const urlPath = (this.router.url.split('?')[0] || '').toLowerCase();
    const segments = urlPath.split('/').filter(Boolean);
    const lastSegment = segments[segments.length - 1];

    if (lastSegment === EN_ACCOMMODATIONS_COLLECTION_SLUG) {
      return EN_ACCOMMODATIONS_COLLECTION_UUID_OVERRIDE;
    }

    return uuid;
  }

  ngOnDestroy(): void {
    if (this.mapFilterHandlerSub) {
      this.mapFilterHandlerSub.unsubscribe();
    }
    if (this.searchControlValueSub) {
      this.searchControlValueSub.unsubscribe();
    }

    if (this.isMobile) {
      window.removeEventListener('scroll', this.preventScrollClose, true);
    }
  }
}
